import { useCallback, useEffect, useState } from "react";
import { Share2, Download, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useStreak } from "@/context/StreakContext";

const W = 1080;
const H = 1080;

const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = "/logo.png";
  });

const flame = (ctx, x, y, s) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(s, s);
  const g = ctx.createLinearGradient(0, -60, 0, 60);
  g.addColorStop(0, "#FDBA74");
  g.addColorStop(0.5, "#FB923C");
  g.addColorStop(1, "#EA580C");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(0, -62);
  ctx.bezierCurveTo(34, -26, 46, -6, 46, 14);
  ctx.bezierCurveTo(46, 42, 24, 62, 0, 62);
  ctx.bezierCurveTo(-24, 62, -46, 42, -46, 14);
  ctx.bezierCurveTo(-46, -10, -28, -22, -14, -46);
  ctx.bezierCurveTo(-10, -30, -4, -22, 0, -62);
  ctx.fill();
  ctx.fillStyle = "#FEF3C7";
  ctx.beginPath();
  ctx.ellipse(0, 26, 15, 22, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

export const ShareStreak = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const { streak, board } = useStreak();
  const [url, setUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const link = `${window.location.origin}/signup?ref=${user?.referral_code || ""}`;
  const days = streak?.current || 0;
  const rank = board?.me?.rank;
  const text = `${days} days in a row talking in 50 languages on ChatMiLu${
    rank ? ` — #${rank} on the streak board` : ""
  }. Come beat me:`;

  const draw = useCallback(async () => {
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#0A0A0B";
    ctx.fillRect(0, 0, W, H);
    const glow = ctx.createRadialGradient(W / 2, 400, 40, W / 2, 400, 620);
    glow.addColorStop(0, "rgba(26,125,255,0.28)");
    glow.addColorStop(1, "rgba(10,10,11,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    ctx.strokeStyle = "rgba(255,255,255,0.10)";
    ctx.lineWidth = 3;
    const r = 56;
    ctx.beginPath();
    ctx.moveTo(48 + r, 48);
    ctx.arcTo(W - 48, 48, W - 48, H - 48, r);
    ctx.arcTo(W - 48, H - 48, 48, H - 48, r);
    ctx.arcTo(48, H - 48, 48, 48, r);
    ctx.arcTo(48, 48, W - 48, 48, r);
    ctx.closePath();
    ctx.stroke();

    flame(ctx, W / 2, 300, 1.9);

    ctx.textAlign = "center";
    ctx.fillStyle = "#ECEEF1";
    ctx.font = "800 210px Helvetica, Arial, sans-serif";
    ctx.fillText(String(days), W / 2, 610);
    ctx.font = "700 54px Helvetica, Arial, sans-serif";
    ctx.fillText(days === 1 ? "day streak" : "day streak", W / 2, 682);

    ctx.font = "500 34px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "#9AA1AB";
    const line = rank
      ? `${user?.name || "I"} · #${rank} on the ChatMiLu streak board`
      : `${user?.name || "Me"} on ChatMiLu`;
    ctx.fillText(line, W / 2, 748);

    ctx.font = "600 38px Helvetica, Arial, sans-serif";
    ctx.fillStyle = "#1A7DFF";
    ctx.fillText("Text anyone, in any language", W / 2, 856);

    const logo = await loadLogo();
    if (logo) {
      const h = 74;
      const w = (logo.width / logo.height) * h;
      ctx.drawImage(logo, (W - w) / 2, 894, w, h);
    } else {
      ctx.fillStyle = "#ECEEF1";
      ctx.font = "800 46px Helvetica, Arial, sans-serif";
      ctx.fillText("ChatMiLu", W / 2, 940);
    }
    ctx.fillStyle = "#6B7280";
    ctx.font = "500 30px Helvetica, Arial, sans-serif";
    ctx.fillText("chatmilu.com", W / 2, 1002);

    canvas.toBlob((b) => {
      if (b) setUrl(URL.createObjectURL(b));
      else console.error("streak card: toBlob returned nothing");
    }, "image/png");
  }, [days, rank, user?.name]);

  useEffect(() => {
    if (open) draw().catch((e) => console.error("streak card draw failed", e));
  }, [open, draw]);

  const blobFile = async () => {
    const res = await fetch(url);
    return new File([await res.blob()], "chatmilu-streak.png", { type: "image/png" });
  };

  const share = async () => {
    const body = `${text} ${link}`;
    try {
      const file = await blobFile();
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: body });
        return;
      }
      if (navigator.share) {
        await navigator.share({ text: body });
        return;
      }
      await navigator.clipboard.writeText(body);
      toast.success("Message and link copied — paste it anywhere");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not open the share sheet");
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(`${text} ${link}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
    toast.success("Copied — your referral link is included");
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "chatmilu-streak.png";
    a.click();
  };

  const social = (href) => window.open(href, "_blank", "noopener,noreferrer");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-streak-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-tight">
            Share your streak
          </DialogTitle>
        </DialogHeader>

        {url && (
          <img
            src={url}
            alt="Your streak card"
            data-testid="share-streak-preview"
            className="w-full rounded-2xl border border-white/10"
          />
        )}
        <p className="text-xs text-ink-soft">
          The link carries your referral code — when a friend joins, you both get Premium days.
        </p>

        <div className="grid grid-cols-2 gap-2">
          <Button
            data-testid="share-streak-share"
            onClick={share}
            className="min-h-[44px] rounded-full bg-brand hover:bg-brand-hover"
          >
            <Share2 className="me-2 h-4 w-4" /> Share
          </Button>
          <Button
            data-testid="share-streak-download"
            variant="outline"
            onClick={download}
            disabled={!url}
            className="min-h-[44px] rounded-full"
          >
            <Download className="me-2 h-4 w-4" /> Save image
          </Button>
          <Button
            data-testid="share-streak-whatsapp"
            variant="outline"
            onClick={() => social(`https://wa.me/?text=${encodeURIComponent(`${text} ${link}`)}`)}
            className="min-h-[44px] rounded-full"
          >
            WhatsApp
          </Button>
          <Button
            data-testid="share-streak-x"
            variant="outline"
            onClick={() =>
              social(
                `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`,
              )
            }
            className="min-h-[44px] rounded-full"
          >
            Post on X
          </Button>
        </div>
        <button
          data-testid="share-streak-copy"
          onClick={copyLink}
          className="mt-1 inline-flex items-center justify-center gap-2 text-sm text-ink-soft underline decoration-dotted hover:text-ink"
        >
          {copied ? <Check className="h-3.5 w-3.5 text-forest" /> : <Copy className="h-3.5 w-3.5" />}
          Copy message and link
        </button>
      </DialogContent>
    </Dialog>
  );
};
